import { useParams, useNavigate } from "react-router-dom";
import Badge, { type BadgeTone } from "@/components/base/Badge";
import { settlements } from "@/mocks/settlements";
import type { Settlement, SettlementFines } from "@/mocks/settlements";

const statusTone: Record<Settlement["status"], BadgeTone> = {
  review: "amber",
  transferred: "green",
  pending: "gray",
  partial: "orange",
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-[#F0FDFD] p-4">
      <p className="text-xs text-ghasla-text-secondary">{label}</p>
      <p className="mt-1 text-xl font-bold text-ghasla-text-heading">{value}</p>
    </div>
  );
}

export default function SettlementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const settlement = settlements.find((s) => String(s.id) === id);

  if (!settlement) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0FDFD]">
          <i className="ri-error-warning-line text-2xl text-ghasla-text-secondary" />
        </div>
        <p className="text-sm font-medium text-ghasla-text-heading">التسوية غير موجودة</p>
        <button
          type="button"
          onClick={() => navigate("/provider/settlements")}
          className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          <i className="ri-arrow-right-line" />
          العودة للتسويات
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[980px]">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/provider/settlements")}
        className="mb-5 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-ghasla-text-secondary transition-colors hover:text-ghasla-text-link"
      >
        <i className="ri-arrow-right-line text-lg" />
        العودة للتسويات
      </button>

      {/* Header */}
      <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-ghasla-text-secondary">كشف التسوية</p>
            <h1 className="mt-1 text-xl font-bold text-ghasla-text-heading md:text-[24px]">
              {settlement.dateRange}
            </h1>
            <div className="mt-2">
              <Badge tone={statusTone[settlement.status]}>{settlement.statusLabel}</Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        {/* Main */}
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <Stat label="إجمالي الطلبات" value={`${settlement.orders}`} />
            <Stat label="إجمالي المبيعات" value={`${settlement.sales} ر.س`} />
          </div>

          {/* Commission */}
          <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                <i className="ri-percent-line text-sm" />
              </span>
              حساب العمولة
            </h3>
            <div className="mt-4 rounded-2xl bg-[#F0FDFD] p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ghasla-text-secondary">
                  العمولة ({settlement.commissionRate}% × المبيعات)
                </span>
                <span className="font-medium text-ghasla-text-primary">-{settlement.commission} ر.س</span>
              </div>
            </div>
          </div>

          {/* Fines */}
          <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                <i className="ri-alert-line text-sm" />
              </span>
              الغرامات والخصومات
            </h3>
            <div className="mt-4">
              {settlement.finesList.length === 0 ? (
                <p className="text-sm text-ghasla-text-secondary">لا توجد غرامات</p>
              ) : (
                <div className="space-y-2">
                  {settlement.finesList.map((f: SettlementFines) => (
                    <div
                      key={f.order}
                      className="flex items-center justify-between rounded-xl border border-[#F4F8F8] p-3 text-sm"
                    >
                      <div>
                        <p className="font-medium text-ghasla-text-primary">{f.reason}</p>
                        <p className="mt-0.5 text-xs text-ghasla-text-secondary">
                          {f.date} · {f.order}
                        </p>
                      </div>
                      <span className="font-semibold text-[#E74C3C]">-{f.amount} ر.س</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Net */}
          <div className="rounded-[20px] border border-[#F0F4F4] bg-[#E8F9F0] p-5 md:p-6">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-ghasla-text-heading">الصافي المستحق</span>
              <span className="text-lg font-bold text-[#27AE60]">{settlement.net} ر.س</span>
            </div>
          </div>

          {/* Disputes */}
          {settlement.disputes.length > 0 && (
            <div className="rounded-[20px] border border-[#F59E0B]/30 bg-[#FFF9EB] p-5 md:p-6">
              {settlement.disputes.map((d) => (
                <div key={d.order} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[#B45309]">
                    {d.order} — {d.amount} ر.س — {d.status}
                  </span>
                  <span className="rounded-full bg-[#F59E0B]/20 px-2 py-0.5 text-xs font-medium text-[#B45309]">
                    المبلغ معلّق
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Bank */}
          {settlement.bank && (
            <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
              <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                  <i className="ri-bank-line text-sm" />
                </span>
                معلومات التحويل البنكي
              </h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-ghasla-text-secondary">البنك</span>
                  <span className="font-medium text-ghasla-text-primary">{settlement.bank.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ghasla-text-secondary">الآيبان</span>
                  <span className="font-medium text-ghasla-text-primary" dir="ltr">{settlement.bank.iban}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ghasla-text-secondary">تاريخ التحويل</span>
                  <span className="font-medium text-ghasla-text-primary">{settlement.bank.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ghasla-text-secondary">المرجع</span>
                  <span className="font-medium text-ghasla-text-primary" dir="ltr">{settlement.bank.ref}</span>
                </div>
              </div>
            </div>
          )}

          {/* Export */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#0D6E6E] px-4 py-3 text-sm font-semibold text-ghasla-text-link transition-colors hover:bg-[#F0FDFD]"
            >
              <i className="ri-file-pdf-2-line text-lg" />
              تحميل كشف PDF
            </button>
            <button
              type="button"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#0D6E6E] px-4 py-3 text-sm font-semibold text-ghasla-text-link transition-colors hover:bg-[#F0FDFD]"
            >
              <i className="ri-file-excel-2-line text-lg" />
              تحميل Excel
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6 lg:sticky lg:top-4">
          <h3 className="text-base font-semibold text-ghasla-text-heading">ملخص التسوية</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-ghasla-text-secondary">الفترة</span>
              <span className="font-medium text-ghasla-text-primary">{settlement.period}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ghasla-text-secondary">الطلبات</span>
              <span className="font-medium text-ghasla-text-primary">{settlement.orders}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ghasla-text-secondary">المبيعات</span>
              <span className="font-medium text-ghasla-text-primary">{settlement.sales} ر.س</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ghasla-text-secondary">العمولة</span>
              <span className="font-medium text-[#E74C3C]">-{settlement.commission} ر.س</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ghasla-text-secondary">الغرامات</span>
              <span className="font-medium text-ghasla-text-primary">
                {settlement.fines === "0" ? "0" : `-${settlement.fines}`} ر.س
              </span>
            </div>
            <div className="border-t border-[#F0F4F4] pt-3">
              <div className="flex justify-between">
                <span className="font-semibold text-ghasla-text-heading">الصافي</span>
                <span className="font-bold text-ghasla-text-link">{settlement.net} ر.س</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}